import { debounce } from 'lodash';
import type { ChangeEvent, InputHTMLAttributes } from 'react';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';
import { TypographyVariant } from '../../utils/theme';
import Icon, { IconName } from '../icon/icon';
import { InputVariant } from '../input';
import { IconWrapper, InputWrapper, StyledInput } from '../input/input.styles';
import Typography from '../typography/typography';

const SEARCH_DEBOUNCE = 500;

export interface ISearchProps extends InputHTMLAttributes<HTMLInputElement> {
  width?: string;
  hideHelpText?: boolean;
}

export const Search = ({
  onChange,
  placeholder,
  disabled,
  width = '335px',
  className,
  hideHelpText,
  ...props
}: ISearchProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [isFocused, setIsFocused] = useState(false);

  const debouncedChange = debounce(
    (event: ChangeEvent<HTMLInputElement>): void => {
      onChange?.(event);
    },
    SEARCH_DEBOUNCE
  );

  return (
    <SearchWrapper className={className}>
      <InputWrapper width={width}>
        <IconWrapper>
          <StyledIcon
            name={IconName.Search}
            color={
              disabled ? theme.palette.neutral[70] : theme.palette.primary.main
            }
          />
        </IconWrapper>
        <StyledInput
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={debouncedChange}
          placeholder={placeholder ?? t('words.search')}
          width={width}
          role="searchbox"
          aria-label={placeholder ?? t('words.search')}
          aria-labelledby={hideHelpText ? undefined : 'results-updated'}
          disabled={disabled}
          className="fs-mask"
          variant={InputVariant.Icon}
          {...props}
        />
      </InputWrapper>
      {!hideHelpText && (
        <TypographyWrapper>
          <AnimatedTypography
            id="results-updated"
            variant={TypographyVariant.bodySm1}
            color={theme.palette.neutral[90]}
            $isFocused={isFocused}
          >
            {t('phrases.resultsUpdatedText', {
              defaultValue: 'Results will be updated as you type',
            })}
          </AnimatedTypography>
        </TypographyWrapper>
      )}
    </SearchWrapper>
  );
};

export default memo(Search);

const SearchWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

const TypographyWrapper = styled.div`
  height: 0.813rem;
`;

const AnimatedTypography = styled(Typography)`
  ${({ $isFocused }: { $isFocused: boolean }) => css`
    text-align: left;
    transition: opacity 0.1s ease-in;
    opacity: ${$isFocused ? 1 : 0};
  `}
`;

const StyledIcon = styled(Icon)`
  ${({ theme }) => css`
    margin: ${theme.spacing(1, 0, -6, 2)};
    z-index: 1;
    display: inline-grid;
  `};
`;
